import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchCurrentUser } from '../userSlice';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Autocomplete,
  Chip,
  Avatar
} from '@mui/material';
import { 
  Business as BusinessIcon, 
  PersonAdd as PersonAddIcon, 
  Add as AddIcon,
  Search as SearchIcon,
  Link as LinkIcon,
  Badge as BadgeIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { createOrganizationForUser } from '../services/organizationService';
import { getOrganizationForJoinRequest, createJoinRequest, getUserPendingJoinRequests, searchOrganizationsByName } from '../services/joinRequestService';
import OnboardingHeader from '../components/OnboardingHeader';

function OrganizationChoicePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  
  const [createOrgDialogOpen, setCreateOrgDialogOpen] = useState(false);
  const [joinOrgDialogOpen, setJoinOrgDialogOpen] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [foundOrganization, setFoundOrganization] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [searchType, setSearchType] = useState('id');
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);

  // Load user's pending join requests on component mount
  useEffect(() => {
    const loadPendingRequests = async () => {
      try {
        const requests = await getUserPendingJoinRequests();
        setPendingRequests(requests);
      } catch (err) {
        console.error('Error loading pending requests:', err);
      } finally {
        setPendingLoading(false);
      }
    };

    loadPendingRequests();
  }, []);

  // Debounced search for name autocomplete
  useEffect(() => {
    if (searchType === 'name' && searchQuery.trim() && searchQuery.length >= 2) {
      const timeoutId = setTimeout(async () => {
        setAutocompleteLoading(true);
        try {
          const results = await searchOrganizationsByName(searchQuery);
          setAutocompleteOptions(results.organizations || []);
        } catch (err) {
          console.error('Error searching organizations:', err);
          setAutocompleteOptions([]);
        } finally {
          setAutocompleteLoading(false);
        }
      }, 300); // 300ms delay

      return () => clearTimeout(timeoutId);
    } else {
      setAutocompleteOptions([]);
    }
  }, [searchQuery, searchType]);

  const handleCreateOrganization = async () => {
    if (!orgName.trim()) {
      setError('Organization name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await createOrganizationForUser({ name: orgName });
      
      enqueueSnackbar('Organization created successfully! Starting setup...', { variant: 'success' });
      
      // Refresh user data
      await dispatch(fetchCurrentUser());
      
      // Close dialog
      setCreateOrgDialogOpen(false);
      setOrgName('');
      
      // Navigate to onboarding
      navigate('/onboarding/welcome');
    } catch (err) {
      console.error('Error creating organization:', err);
      setError(err.response?.data?.error || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchOrganization = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a search query');
      return;
    }

    setSearchLoading(true);
    setError('');

    try {
      const results = await getOrganizationForJoinRequest(searchQuery.trim(), searchType);
      
      if (results.type === 'single') {
        setFoundOrganization(results.organization);
      } else {
        // For multiple results, user needs to select from autocomplete
        setError('Please select an organization from the suggestions');
        setFoundOrganization(null);
      }
      
      if (results.type === 'single') {
        setError('');
      }
    } catch (err) {
      console.error('Error searching organization:', err);
      setError(err.response?.data?.error || 'Organization not found');
      setFoundOrganization(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAutocompleteChange = (event, value) => {
    if (value) {
      setSearchQuery(value.name);
      setFoundOrganization(value);
      setError('');
    } else {
      setFoundOrganization(null);
    }
  };

  const handleJoinOrganization = async () => {
    if (!foundOrganization) {
      setError('Please search for an organization first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await createJoinRequest(foundOrganization._id);
      
      enqueueSnackbar('Join request submitted successfully! The organization admin will review your request.', { variant: 'success' });
      
      // Close dialog and reset state
      setJoinOrgDialogOpen(false);
      setOrganizationId('');
      setFoundOrganization(null);
      
      // Navigate to a waiting page or stay on current page
      navigate('/organization-choice');
    } catch (err) {
      console.error('Error submitting join request:', err);
      setError(err.response?.data?.error || 'Failed to submit join request');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrgClick = () => {
    setCreateOrgDialogOpen(true);
    setError('');
  };

  const handleJoinOrgClick = () => {
    setJoinOrgDialogOpen(true);
    setError('');
  };

  const handleCloseCreateDialog = () => {
    setCreateOrgDialogOpen(false);
    setOrgName('');
    setError('');
  };

  const handleCloseJoinDialog = () => {
    setJoinOrgDialogOpen(false);
    setSearchQuery('');
    setFoundOrganization(null);
    setError('');
    setSearchType('id');
    setAutocompleteOptions([]);
  };

  const handleSearchTypeChange = (event) => {
    setSearchType(event.target.value);
    setSearchQuery('');
    setFoundOrganization(null);
    setError('');
    setAutocompleteOptions([]);
  };

  return (
    <>
      <OnboardingHeader />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }} elevation={2}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Welcome to Meeting Room Manager
            </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Choose how you'd like to get started
          </Typography>
          <Divider sx={{ my: 3 }} />
        </Box>

        {/* Pending Join Requests Section */}
        {!pendingLoading && pendingRequests.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Pending Join Requests
              </Typography>
              <Typography variant="body2">
                You have {pendingRequests.length} pending join request(s). You can still create a new organization or request to join another organization while waiting for approval.
              </Typography>
            </Alert>
            
            {pendingRequests.map((request) => (
              <Box key={request._id} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={request.organizationId?.logo?.thumbnailUrl || request.organizationId?.logo?.url}
                    sx={{ width: 32, height: 32 }}
                  >
                    <BusinessIcon />
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {request.organizationId?.name || 'Unknown Organization'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Request submitted: {new Date(request.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
              onClick={handleCreateOrgClick}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 4 }}>
                <Box sx={{ mb: 3 }}>
                  <BusinessIcon sx={{ fontSize: 80, color: 'primary.main' }} />
                </Box>
                <Typography variant="h5" component="h2" gutterBottom>
                  Create New Organization
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Set up a new organization for your team. You'll be the admin and can invite team members later.
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  size="large"
                  onClick={handleCreateOrgClick}
                >
                  Create Organization
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
              onClick={handleJoinOrgClick}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 4 }}>
                <Box sx={{ mb: 3 }}>
                  <PersonAddIcon sx={{ fontSize: 80, color: 'primary.main' }} />
                </Box>
                <Typography variant="h5" component="h2" gutterBottom>
                  Join Existing Organization
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Search for an organization by ID or name to request to join.
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<SearchIcon />}
                  size="large"
                  onClick={handleJoinOrgClick}
                >
                  Join Organization
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>

        {/* Create Organization Dialog */}
        <Dialog open={createOrgDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Organization</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Organization Name"
              fullWidth
              variant="outlined"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Enter your organization name"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog}>Cancel</Button>
            <Button 
              onClick={handleCreateOrganization} 
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Organization'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Join Organization Dialog */}
        <Dialog open={joinOrgDialogOpen} onClose={handleCloseJoinDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Join Organization</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddIcon color="primary" />
                How to join an organization
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      border: '2px solid',
                      borderColor: 'success.main',
                      bgcolor: 'success.50'
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <LinkIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                      <Chip 
                        label="EASIEST" 
                        color="success" 
                        size="small" 
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Direct Join Link
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ask your admin for a join link - just click and you're in!
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      border: '2px solid',
                      borderColor: searchType === 'id' ? 'primary.main' : 'grey.300',
                      bgcolor: searchType === 'id' ? 'primary.50' : 'grey.50'
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <BadgeIcon sx={{ 
                        fontSize: 40, 
                        color: searchType === 'id' ? 'primary.main' : 'grey.600', 
                        mb: 1 
                      }} />
                      <Chip 
                        label="EXACT" 
                        color={searchType === 'id' ? 'primary' : 'default'}
                        size="small" 
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Organization ID
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        24-character code for exact match
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      border: '2px solid',
                      borderColor: searchType === 'name' ? 'secondary.main' : 'grey.300',
                      bgcolor: searchType === 'name' ? 'secondary.50' : 'grey.50'
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <AutoAwesomeIcon sx={{ 
                        fontSize: 40, 
                        color: searchType === 'name' ? 'secondary.main' : 'grey.600', 
                        mb: 1 
                      }} />
                      <Chip 
                        label="SMART" 
                        color={searchType === 'name' ? 'secondary' : 'default'}
                        size="small" 
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Organization Name
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Real-time suggestions as you type
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Search Type Selection */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Choose your search method:
              </Typography>
              <RadioGroup
                row
                value={searchType}
                onChange={handleSearchTypeChange}
                sx={{ gap: 2 }}
              >
                <FormControlLabel 
                  value="id" 
                  control={<Radio color="primary" />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BadgeIcon color="primary" />
                      <Typography variant="body2" fontWeight="bold">
                        Search by ID
                      </Typography>
                    </Box>
                  }
                  sx={{
                    margin: 0,
                    padding: 1,
                    borderRadius: 1,
                    border: '2px solid',
                    borderColor: searchType === 'id' ? 'primary.main' : 'grey.300',
                    bgcolor: searchType === 'id' ? 'primary.50' : 'transparent'
                  }}
                />
                <FormControlLabel 
                  value="name" 
                  control={<Radio color="secondary" />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AutoAwesomeIcon color="secondary" />
                      <Typography variant="body2" fontWeight="bold">
                        Search by Name
                      </Typography>
                    </Box>
                  }
                  sx={{
                    margin: 0,
                    padding: 1,
                    borderRadius: 1,
                    border: '2px solid',
                    borderColor: searchType === 'name' ? 'secondary.main' : 'grey.300',
                    bgcolor: searchType === 'name' ? 'secondary.50' : 'transparent'
                  }}
                />
              </RadioGroup>
            </Box>

            {/* Organization Search */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                {searchType === 'id' ? 'Enter Organization ID:' : 'Search by Organization Name:'}
              </Typography>
              
              {searchType === 'id' ? (
                <Paper elevation={2} sx={{ p: 2, border: '2px solid', borderColor: 'primary.main' }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      autoFocus
                      label="Organization ID"
                      fullWidth
                      variant="outlined"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Enter the organization ID"
                      helperText="24-character ID (e.g., 507f1f77bcf86cd799439011)"
                      InputProps={{
                        startAdornment: <BadgeIcon color="primary" sx={{ mr: 1 }} />
                      }}
                    />
                    <Button 
                      onClick={handleSearchOrganization}
                      variant="contained"
                      disabled={searchLoading || !searchQuery.trim()}
                      sx={{ minWidth: 120, height: 56 }}
                      startIcon={<SearchIcon />}
                    >
                      {searchLoading ? 'Searching...' : 'Search'}
                    </Button>
                  </Box>
                </Paper>
              ) : (
                <Paper elevation={2} sx={{ p: 2, border: '2px solid', borderColor: 'secondary.main' }}>
                  <Autocomplete
                    options={autocompleteOptions}
                    loading={autocompleteLoading}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    onChange={handleAutocompleteChange}
                    onInputChange={(event, newInputValue) => {
                      setSearchQuery(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Organization Name"
                        fullWidth
                        variant="outlined"
                        placeholder="Type to search organizations..."
                        helperText="Type at least 2 characters to see suggestions"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <AutoAwesomeIcon color="secondary" sx={{ mr: 1 }} />
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Avatar
                            src={option.logo?.thumbnailUrl || option.logo?.url}
                            sx={{ width: 40, height: 40 }}
                          >
                            <BusinessIcon color="secondary" />
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body1" fontWeight="bold">
                              {option.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.city && option.country ? `${option.city}, ${option.country}` : 
                               option.city ? option.city : 
                               option.country ? option.country : 
                               'Location not specified'}
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    )}
                    noOptionsText={
                      searchQuery.length < 2 
                        ? "Type at least 2 characters to search" 
                        : "No organizations found"
                    }
                  />
                </Paper>
              )}
            </Box>

            {/* Found Organization Display */}
            {foundOrganization && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={foundOrganization.logo?.thumbnailUrl || foundOrganization.logo?.url}
                    sx={{ width: 32, height: 32 }}
                  >
                    <BusinessIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body1">
                      Organization found: <strong>{foundOrganization.name}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {foundOrganization.city && foundOrganization.country ? `${foundOrganization.city}, ${foundOrganization.country}` : 
                       foundOrganization.city ? foundOrganization.city : 
                       foundOrganization.country ? foundOrganization.country : 
                       'Location not specified'}
                    </Typography>
                  </Box>
                </Box>
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseJoinDialog}>Cancel</Button>
            <Button 
              onClick={handleJoinOrganization} 
              variant="contained"
              disabled={loading || !foundOrganization}
            >
              {loading ? 'Submitting...' : 'Submit Join Request'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
    </>
  );
}

export default OrganizationChoicePage; 